/**
 * Barras por día, para los envíos de formulario de una página.
 *
 * Barras y no línea a propósito: los envíos son sucesos contables y sueltos —
 * tres un martes, ninguno el miércoles. Una línea entre esos puntos dibuja una
 * pendiente continua que sugiere valores intermedios que no existen; las barras
 * dicen "esto pasó aquí, y aquí no pasó nada".
 */
import { useEffect, useRef, useState } from 'react'
import { activeLanguage, useI18n } from '../../lib/i18n'
import { formatNumber } from '../../lib/numbers'
import { intlLocale } from '@webbuilder/i18n'

const HEIGHT = 190
const PAD = { top: 10, right: 12, bottom: 26, left: 40 }
/** Por debajo de esto una barra deja de verse; mejor una marca fina que nada. */
const MIN_BAR = 2

function dayLabel(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(intlLocale(activeLanguage()), {
    day: 'numeric', month: 'short', timeZone: 'UTC',
  })
}

function tooltipDay(day: string): string {
  const label = new Date(`${day}T00:00:00Z`).toLocaleDateString(activeLanguage(), {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
  })
  return label.charAt(0).toLocaleUpperCase() + label.slice(1)
}

/* La etiqueta llega como CLAVE y no como texto: el valor por defecto de un
   parámetro se evalúa fuera de cualquier componente, así que escribirlo aquí lo
   dejaba en español pasara lo que pasara. */
export function BarChart({ series, labelKey = 'barChart.submissions', loading = false }: {
  series: { day: string; total: number }[]
  labelKey?: string
  loading?: boolean
}) {
  const { t } = useI18n()
  const label = t(labelKey)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [hover, setHover] = useState<number | null>(null)
  const [tipWidth, setTipWidth] = useState(0)

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => setWidth(entries[0]?.contentRect.width ?? 0))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const innerW = Math.max(width - PAD.left - PAD.right, 0)
  const innerH = HEIGHT - PAD.top - PAD.bottom
  const baseline = PAD.top + innerH
  const peak = Math.max(...series.map((point) => point.total), 0)
  // El eje sube al menos a 1: con todo a cero, dividir por el máximo daría NaN
  // y además una escala 0–0 no dice nada.
  const top = Math.max(peak, 1)

  const slot = series.length > 0 ? innerW / series.length : 0
  // Las barras dejan aire entre sí, pero nunca menos de 1px de ancho útil.
  const barW = Math.max(Math.min(slot * 0.62, 26), 1)
  const xAt = (i: number) => PAD.left + slot * i + (slot - barW) / 2
  const heightAt = (value: number) => (value <= 0 ? 0 : Math.max((value / top) * innerH, MIN_BAR))

  const ticks = [...new Set([0, Math.round(top / 2), top])]
  const labelIndexes = series.length <= 1
    ? [0]
    : [...new Set([0, Math.floor((series.length - 1) / 2), series.length - 1])]

  const active = hover === null ? null : series[hover]

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: HEIGHT }}>
      {loading ? (
        <div className="sk dash-analytics-chart-skeleton" aria-hidden="true" />
      ) : width > 0 ? (
        <svg width={width} height={HEIGHT} role="img" aria-label={t('barChart.perDay', { label })}>
          {ticks.map((value) => {
            const y = baseline - (value / top) * innerH
            return (
              <g key={value}>
                <line x1={PAD.left} y1={y} x2={width - PAD.right} y2={y} stroke="var(--dash-border)" strokeWidth={1} />
                <text x={PAD.left - 10} y={y} textAnchor="end" dominantBaseline="middle" fill="var(--dash-muted)" fontSize={11}>
                  {value}
                </text>
              </g>
            )
          })}

          {series.map((point, i) => (
            <rect
              key={point.day}
              className={`dash-bar${hover === i ? ' is-active' : ''}`}
              x={xAt(i)}
              y={baseline - heightAt(point.total)}
              width={barW}
              height={heightAt(point.total)}
              rx={Math.min(3, barW / 2)}
            />
          ))}

          {labelIndexes.map((i) => {
            const point = series[i]
            if (!point) return null
            return (
              <text
                key={point.day}
                x={xAt(i) + barW / 2}
                y={HEIGHT - 6}
                textAnchor={i === 0 ? 'start' : i === series.length - 1 ? 'end' : 'middle'}
                fill="var(--dash-muted)"
                fontSize={11}
              >
                {dayLabel(point.day)}
              </text>
            )
          })}

          {/* Una banda invisible por día: apuntar a una barra de 3px de alto —o
              a un día sin barra— sería imposible si el objetivo fuera la barra. */}
          {series.map((point, i) => (
            <rect
              key={`hit-${point.day}`}
              x={PAD.left + slot * i}
              y={PAD.top}
              width={slot}
              height={innerH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </svg>
      ) : null}

      {!loading && active ? (
        <div
          ref={(node) => { if (node) setTipWidth(node.offsetWidth) }}
          className="dash-card-meta dash-chart-tip is-bars"
          style={{
            position: 'absolute',
            left: Math.min(
              Math.max(xAt(hover ?? 0) + barW / 2, tipWidth / 2),
              Math.max(width - tipWidth / 2, tipWidth / 2),
            ),
            top: Math.max(baseline - heightAt(active.total) - 12, 0),
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="dash-chart-tip-day">{tooltipDay(active.day)}</div>
          <div className="dash-chart-tip-barline">
            <strong>{formatNumber(active.total)}</strong> {label}
          </div>
        </div>
      ) : null}
    </div>
  )
}
